import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { geocodeAddress } from "@/lib/pricing";

// Champ adresse avec autocomplete (Nominatim / OpenStreetMap).
// Debounce 500 ms. La sélection remonte les coordonnées au parent.
const AddressInput = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  onSelect,
  testid,
  dark = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    onSelect(null);
    clearTimeout(timerRef.current);
    if (q.trim().length < 4) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const results = await geocodeAddress(q, controller.signal);
        setSuggestions(results);
        setOpen(true);
      } catch {
        /* requête annulée ou réseau indisponible */
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  const choose = (s) => {
    onChange(s.label);
    onSelect({ label: s.label, lat: s.lat, lon: s.lon });
    setOpen(false);
    setSuggestions([]);
  };

  const inputClasses = dark
    ? "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 pl-11 text-base text-[#FAFAF7] placeholder:text-slate-500 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40"
    : "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 pl-11 text-base text-foreground placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div ref={boxRef} className="relative">
      <label
        htmlFor={id}
        className={`mb-2 block text-sm font-medium ${dark ? "text-slate-300" : "text-slate-700"}`}
      >
        {label}
      </label>
      <div className="relative">
        <MapPin
          className={`pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 ${
            dark ? "text-slate-500" : "text-slate-400"
          }`}
        />
        <input
          id={id}
          type="text"
          data-testid={testid}
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className={inputClasses}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
        />
        {loading && (
          <Loader2
            className="absolute right-3.5 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-slate-400"
            aria-hidden="true"
          />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
        >
          {suggestions.map((s, i) => (
            <li key={`${s.lat}-${s.lon}-${i}`} role="option" aria-selected="false">
              <button
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onClick={() => choose(s)}
                className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-primary/5 focus:bg-primary/10 focus:outline-none"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="line-clamp-2">{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressInput;
